import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { bookingInquiryApis, dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import {
  customsInstructionApis,
  dashboardCustomsInstructionApis,
} from '@apis/next/customsInstructionApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import infoIcon from '@assets/img/icons/info.svg';
import { BookingStatusFlows } from '@components/admin/booking/BookingStatusFlows';
import { CustomsRequestFormDialog } from '@components/admin/booking/CustomsRequestFormDialog';
import { FormTitle } from '@components/admin/Forms';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { DebugForm } from '@components/NextForms/DebugForm';
import { FileArrayInput } from '@components/NextForms/FileArrayInput';
import { FileInput } from '@components/NextForms/FileInput';
import { ManagementFormContainer as OldManagementFormContainer } from '@components/NextForms/ManagementForm';
import { ManagementFormLabel } from '@components/NextForms/ManagementForm';
import { QueryContainer } from '@components/QueryContainer';
import { CommodityArrayInput2 } from '@components/utilities/Bookings/CommodityArrayInput';
import { CustomFilesInformation } from '@components/utilities/Bookings/CustomFilesInformation';
import { CustomsInstructionAlertDialog } from '@components/utilities/Bookings/CustomsInstructionAlertDialog';
import { CustomsInstructionConsigneeDisplay } from '@components/utilities/Bookings/CustomsInstructionConsigneeDisplay';
import { CustomsInstructionFiles } from '@components/utilities/Bookings/CustomsInstructionFiles';
import { CustomsInstructionInsurance } from '@components/utilities/Bookings/CustomsInstructionInsurance';
import { CustomsInstructionSpecialNote } from '@components/utilities/Bookings/CustomsInstructionSpecialNote';
import {
  CustomsInstructionContext,
  InstructionSuccessfulAction,
  type CustomsInstructionContextValue,
  type CustomsInstructionContextValueBase,
} from '@components/utilities/Bookings/helpers2';
import { InstructionChangeRequiredForm } from '@components/utilities/Bookings/InstructionChangeRequiredForm2';
import {
  InstructionBarContainer,
  InstructionBarTitle,
  InstructionContainer,
  InstructionHint,
  InstructionInputContainer,
  InstructionRedirectLink,
  InstructionSplit,
} from '@components/utilities/Bookings/instructionComponents';
import {
  InstructionFlow,
  InstructionFlowState,
  InstructionFlowStepOption,
} from '@components/utilities/Bookings/InstructionFlow';
import { calendarStrings } from '@data/calendar';
import { getInstructionStatusMeta, instructionStrings } from '@data/instruction';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useBookingInquiryDetail } from '@hooks/queries/useBookingInquiryDetail';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { useIsAdmin } from '@hooks/useIsAdmin';
import {
  AdminInquiryDetail,
  AdminInquiryDetailHelper,
  BaseInquiryDetail,
} from '@models/BookingInquiry';
import {
  CustomsInstruction,
  CustomsInstructionForm,
  CustomsInstructionInsuranceForm,
} from '@models/next/CustomsInstruction';
import { CustomsInstructionHelper } from '@models/next/CustomsInstructionHelper';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { User } from '@models/User';
import { getDecodeResult, unwrapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { useCommonStyles, makeMuiStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { firstLeftEither, foldMessagesEither, makeMessagesEitherRight } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { mapOptional } from '@utils/types';
import { useShallowMemo } from '@utils/useShallowMemo';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { pipe } from 'remeda';
import type { SafeParseReturnType } from 'zod';

import { url } from '../../../../.split-pages/meta';

const strings = createStrings({
  ja: {
    afterDeadlineHint:
      '締め切り日が過ぎても、アップロードは可能ですが、納期及び保税倉庫での保管費用が発生するなど恐れがあります。ご了承ください。',
    customsFilesUploadHint:
      '*パッキングリスト、インボイス、評価書類など、アップロードしてください。',
    status: 'ステータス',
    pleaseUploadCustomsFileAndCommodityInfo: '通関書類と商品説明をアップロードしてください。',
    rcepFileUploadHint: () => (
      <>
        *RCEPの利用には<b>原産地証明書</b>
        をご用意いただく必要があります。原産地証明書は輸出する物品の原産地を証明するための書類で、原則輸出者が用意します。
      </>
    ),
    customsInstructionFlow: `${instructionStrings.customsInstruction}の流れ`,
    overdueMessage:
      '本件貨物は日本の港に入港しました、取り急ぎ通関書類のアップロードと海上保険の確認をしてください。',
    successMessage: (d2dNo: string) => ({
      save: '入力した内容が保存されました。',
      submit:
        '送信されました。送信済の通関書類、もしくは海上保険を変更する場合、左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。',
      createCustomsRequest: (
        <>
          通関依頼書が保存されました。詳しくは右側「通関資料」{'>'}
          <a
            href={`${url('/admin/booking/CustomsRequestDetails')}?d2d_no=${d2dNo}`}
            target="_blank"
          >
            「通関依頼書」
          </a>
          をご覧ください。
        </>
      ),
      submitCustomsRequest: (
        <>
          通関依頼書が送信されました。詳しくは右側「通関資料」{'>'}
          <a
            href={`${url('/admin/booking/CustomsRequestDetails')}?d2d_no=${d2dNo}`}
            target="_blank"
          >
            「通関依頼書」
          </a>
          をご覧ください。
        </>
      ),
      urlSubmitCustomsRequest: (
        <>
          通関依頼書のリンクがコピーされました。詳しくは右側「通関資料」{'>'}
          <a
            href={`${url('/admin/booking/CustomsRequestDetails')}?d2d_no=${d2dNo}`}
            target="_blank"
          >
            「通関依頼書」
          </a>
          をご覧ください。
        </>
      ),
      requireChange: `修正依頼が送信されました。`,
    }),
    confirmed: '確認済み',
    confirmAndRequest: '確認済み、通関依頼書を作成',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  topGuidance: {
    ...common.textGray800,
    ...common.smallerText,
    ...common.fontWeight700,
    padding: '0.25rem 0 1rem 0',
  },
  clientActionsContainer: {
    ...common.flexColumn,
    rowGap: 10,
  },
}));

const ManagementFormContainer: typeof OldManagementFormContainer = (props) => {
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  return (
    <OldManagementFormContainer
      {...props}
      labelProps={{ className: classes.textGray700Important }}
      containerProps={{
        style: {
          paddingBottom: '2rem',
        },
      }}
    />
  );
};

const ManagementInfoBar: React.FC = () => {
  const isAdmin = useIsAdmin();
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  const { d2dNo, customsInstruction } = useContext(CustomsInstructionContext);

  const isOverdue = useMemo(
    () => CustomsInstructionHelper.isOverdue(customsInstruction),
    [customsInstruction],
  );

  return (
    <InstructionBarContainer
      title={isAdmin ? instructionStrings.managementInformation : instructionStrings.deadline}
    >
      {isAdmin && (
        <div className={classes.middleText} style={{ marginBottom: '0.5rem' }}>
          {instructionStrings.deadline}
        </div>
      )}

      <div
        className={clsx(
          isOverdue && classes.textError,
          classes.middleText,
          classes.textPrimary,
          classes.fontWeight600Important,
        )}
        style={{ marginBottom: '0.625rem' }}
      >
        {calendarStrings.yearMonthDay(new Date(customsInstruction.cutoff_date))}
        {` 16:00`}
        {formStrings.until}
      </div>

      {!isAdmin && (
        <div className={clsx(classes.flexInline, classes.itemsStart)}>
          <img alt="Info" src={infoIcon} style={{ paddingRight: 6, paddingTop: 3 }} />
          <div
            className={clsx(classes.smallerText, classes.textGray700)}
            style={{ marginBottom: '0.75rem' }}
          >
            {strings.afterDeadlineHint}
          </div>
        </div>
      )}

      <InstructionBarTitle>{strings.status}</InstructionBarTitle>
      <BookingStatusFlows d2dNo={d2dNo} select={{ customs_status_flow: true }} noHeader />
    </InstructionBarContainer>
  );
};

const AdminActionsBar: React.FC = () => {
  const {
    form,
    d2dNo,
    isEditing,
    setIsEditing,
    canEditAsSalesperson,
    customsInstruction,
    onRefresh,
    setSuccessfulAction,
  } = useContext(CustomsInstructionContext);

  const { confirm, renderDialog } = useConfirmDialog();

  const [changeRequiredOpen, setChangeRequiredOpen] = useState(false);

  const canConfirm = useMemo(
    () =>
      firstLeftEither(
        () => canEditAsSalesperson,
        () => CustomsInstructionHelper.canConfirm(customsInstruction),
      ),
    [customsInstruction],
  );

  const canRequireChange = useMemo(
    () =>
      firstLeftEither(
        () => canEditAsSalesperson,
        () => CustomsInstructionHelper.canRequireChange(customsInstruction),
      ),
    [customsInstruction],
  );

  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  return (
    <>
      {renderDialog()}

      <InstructionBarContainer noTitle>
        <div className={classes.flexColumn} style={{ rowGap: '0.5rem' }}>
          {changeRequiredOpen && (
            <InstructionChangeRequiredForm
              type="customsInstruction"
              onClose={() => setChangeRequiredOpen(false)}
              onSuccess={async () => {
                onRefresh?.();
                setChangeRequiredOpen(false);
                setSuccessfulAction('requireChange');
              }}
              d2dNo={d2dNo}
            />
          )}

          <Button
            color={foldMessagesEither(
              canRequireChange,
              () => 'grey',
              () => 'primary',
            )}
            size="small"
            text={instructionStrings.requireChange}
            type="button"
            onClick={() => {
              foldMessagesEither(
                canRequireChange,
                (e) => {
                  confirm({
                    message: e.messages,
                  });
                },
                () => {
                  setChangeRequiredOpen(true);
                },
              );
            }}
          />

          {isEditing ? (
            <Button
              color="primary"
              text={formStrings.save}
              size="small"
              /**
               *  Cannot use type="submit" here,
               *  seems after clicking the isEditing === false button,
               *  will make it "submit" and triggers the form submission
               */
              type="button"
              onClick={() => {
                form.root.fields.action.setOutput('save');
                form.element?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                );
              }}
            />
          ) : (
            <Button
              color={foldMessagesEither(
                canEditAsSalesperson,
                () => 'grey',
                () => 'primary',
              )}
              size="small"
              type="button"
              text={formStrings.edit}
              onClick={() =>
                foldMessagesEither(
                  canEditAsSalesperson,
                  (e) => {
                    confirm({
                      message: e.messages,
                    });
                  },
                  () => {
                    setIsEditing(true);
                  },
                )
              }
            />
          )}

          <Button
            color={foldMessagesEither(
              canConfirm,
              () => 'grey',
              () => 'accent',
            )}
            size="small"
            type="button"
            disabled={isEditing}
            text={foldMessagesEither(
              canConfirm,
              () => strings.confirmed,
              () => strings.confirmAndRequest,
            )}
            onClick={() =>
              foldMessagesEither(
                canConfirm,
                (e) => {
                  confirm({
                    message: e.messages,
                  });
                },
                () => {
                  form.root.fields.action.setOutput('attempt');
                  form.element?.dispatchEvent(
                    new Event('submit', { cancelable: true, bubbles: true }),
                  );
                },
              )
            }
          />
        </div>
      </InstructionBarContainer>

      <InstructionBarContainer noTitle>
        <CustomsInstructionConsigneeDisplay />
      </InstructionBarContainer>

      <InstructionBarContainer noTitle>
        <CustomsInstructionFiles />
      </InstructionBarContainer>
    </>
  );
};

const ClientInstructionFlow: React.FC = observer(() => {
  const steps = useMemo(
    () =>
      [
        {
          name: 'custom_files' as const,
          title: instructionStrings.customsFiles,
        },
        {
          name: 'commodities' as const,
          title: instructionStrings.commodities,
        },
        {
          name: 'has_special_note' as const,
          title: instructionStrings.specialNote,
        },
        {
          name: 'special_note' as const,
          title: instructionStrings.specialNoteDetails,
        },
        {
          name: 'insurance' as const,
          title: instructionStrings.seaInsuranceUsage,
        },
        {
          name: 'rcep_file' as const,
          title: instructionStrings.rcepUsage,
        },
      ] satisfies InstructionFlowStepOption[],
    [],
  );

  const { form, baseInquiryDetail } = useContext(CustomsInstructionContext);
  const { fields } = form.root;

  const getInstructionFlowState = (): InstructionFlowState => {
    const result = CustomsInstructionForm.safeParse({
      ...(unwrapDecodeResult(form.looseInput) as any),
      action: 'submit',
    });

    return {
      hiddenSteps: [
        !baseInquiryDetail.use_rcep && 'rcep_file',
        !getDecodeResult(fields.has_special_note.decodeResult) && 'special_note',
      ].filter((x): x is string => Boolean(x)),
      completeSteps: result.success
        ? steps.map((s) => s.name)
        : pipe(
            result.error,
            (e) => e.format(),
            (e) =>
              [
                !e.custom_files && 'custom_files',
                !e.commodities && 'commodities',
                !e.has_special_note && 'has_special_note',
                !e.special_note && 'special_note',
                !e.insurance && 'insurance',
                !e.rcep_file && 'rcep_file',
              ].filter((x): x is string => Boolean(x)),
          ),
    };
  };

  return (
    <InstructionFlow
      steps={steps}
      state={getInstructionFlowState()}
      formSubmitted={form.submitCount > 0}
    />
  );
});

const ClientActionsBar: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form, d2dNo, customsInstruction, isEditing, setIsEditing, onGoBack } =
    useContext(CustomsInstructionContext);
  const { fields } = form.root;

  const currentStatus = useMemo(
    () => CustomsInstructionHelper.getCurrentVisibleStatus(customsInstruction)!,
    [customsInstruction],
  );

  return (
    <>
      <InstructionBarContainer title={strings.customsInstructionFlow}>
        <ClientInstructionFlow />
      </InstructionBarContainer>

      <div className={classes.clientActionsContainer}>
        {isEditing ? (
          <>
            <Button
              color="primary"
              size="small"
              text={formStrings.temporarilySave}
              type="button"
              onClick={() => {
                fields.action.setOutput('save');
                form.element?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                );
              }}
            />

            <Button
              color="primary"
              size="small"
              text={formStrings.submit}
              type="button"
              onClick={() => {
                fields.action.setOutput('attempt');
                form.element?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                );
              }}
            />
          </>
        ) : (
          <>
            {getInstructionStatusMeta(currentStatus.name).editable && (
              <Button
                color="primary"
                size="small"
                text={formStrings.edit}
                type="button"
                onClick={() => setIsEditing(true)}
              />
            )}
          </>
        )}

        <Button color="outlined" size="small" text={formStrings.prev} onClick={onGoBack} />
      </div>

      <InstructionRedirectLink d2dNo={d2dNo} variant="customsInstruction" />
    </>
  );
});

const WithQueries = ({
  customsInstruction,
  user,
  adminPreAlert,
  adminBookingInquiry,
  baseInquiryDetail: _baseInquiryDetail,
  d2dNo,
  onGoBack,
  onRefresh,
}: {
  customsInstruction: CustomsInstruction;
  user: User;
  adminPreAlert?: PreAlertDetails;
  adminBookingInquiry?: AdminInquiryDetail;
  baseInquiryDetail?: BaseInquiryDetail;
} & CustomsInstructionContextValueBase) => {
  const isAdmin = useIsAdmin();
  const classes = {
    ...useStyles(),
    ...useCommonStyles(),
  };

  const savedInsuranceRef = useRef<CustomsInstructionInsuranceForm[]>([]);

  const initialOutput = useMemo(
    () => CustomsInstructionHelper.mapToForm(customsInstruction),
    [customsInstruction],
  );

  const form = useForm(CustomsInstructionForm, {
    initialOutput,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const currentStatus = useMemo(
    () => CustomsInstructionHelper.getCurrentVisibleStatus(customsInstruction)!,
    [customsInstruction],
  );

  const isOverdue = useMemo(
    () => CustomsInstructionHelper.isOverdue(customsInstruction),
    [customsInstruction],
  );

  const adminInquiryDetailHelper = useMemo(
    () => mapOptional(adminBookingInquiry, (b) => new AdminInquiryDetailHelper(b)),
    [adminBookingInquiry],
  );
  const canEditAsSalesperson = useMemo(
    () =>
      (adminPreAlert && adminInquiryDetailHelper?.canEdit(user, adminPreAlert)) ??
      makeMessagesEitherRight(),
    [user, adminInquiryDetailHelper, adminPreAlert],
  );

  const initialIsEditing = getInstructionStatusMeta(currentStatus.name).editable && !isAdmin;

  const [isEditing, setIsEditing] = useState(initialIsEditing);

  const [alertOpen, setAlertOpen] = useState(false);

  const [parsed, setParsed] =
    useState<
      SafeParseReturnType<
        (typeof CustomsInstructionForm)['_input'],
        (typeof CustomsInstructionForm)['_output']
      >
    >();

  const [showRcepAlert, setShowRcepAlert] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);

  const [successfulAction, setSuccessfulAction] = useState<InstructionSuccessfulAction>();

  const baseInquiryDetail = (_baseInquiryDetail ?? adminBookingInquiry)!;

  const contextValue: CustomsInstructionContextValue = useShallowMemo(() => ({
    savedInsuranceRef,
    customsInstruction,
    user,
    form,
    isEditing,
    setIsEditing,
    d2dNo,
    onGoBack,
    onRefresh,
    canEditAsSalesperson,
    baseInquiryDetail,
    successfulAction,
    setSuccessfulAction,
  }));

  const mode = isEditing ? 'edit' : 'display';

  const fileUpload = fileUploadApis.useMutation('post', '/file/upload');

  const patchCustomsInstruction = customsInstructionApis.usePatch('/customs-instruction/:d2d_no', {
    params: { d2d_no: d2dNo },
  });

  const dashboardPatchCustomsInstruction = dashboardCustomsInstructionApis.usePatch(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: { d2d_no: d2dNo },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [patchCustomsInstruction, dashboardPatchCustomsInstruction, fileUpload],
  });

  const reset = () => {
    setSuccessfulAction(undefined);
    fileUpload.reset();
    dashboardPatchCustomsInstruction.reset();
    patchCustomsInstruction.reset();
  };

  return (
    <>
      {isAdmin && (
        <CustomsRequestFormDialog
          type="create"
          open={requestDialogOpen}
          onClose={() => setRequestDialogOpen(false)}
          onSuccess={(action) => {
            setSuccessfulAction(action);
            onRefresh?.();
          }}
          customsInstruction={customsInstruction}
          d2dNo={d2dNo}
        />
      )}

      <form
        {...form.bindForm({
          async onSubmitError() {
            reset();
            const action = getDecodeResult(fields.action.decodeResult);
            if (action === 'attempt') {
              setAlertOpen(true);
              setShowRcepAlert(false);
              setParsed(form.parsed);
            }
          },
          async onSubmit(values) {
            const getPatchData = () =>
              CustomsInstructionHelper.uploadForm(values, fileUpload.mutateAsync);

            reset();

            const onSubmitSuccess = () => {
              form.root._walk((f) => f.setTouched(false));
              setIsEditing(false);
              if (values.action === 'save') {
                setSuccessfulAction('save');
              } else {
                setSuccessfulAction('submit');
              }
              onRefresh?.();
            };

            if (values.action === 'attempt') {
              if (!values.rcep_file && baseInquiryDetail.use_rcep) {
                setAlertOpen(true);
                setShowRcepAlert(true);
                setParsed(undefined);
                return;
              }
            }

            switch (values.action) {
              case 'save':
                if (isAdmin) {
                  await dashboardPatchCustomsInstruction.mutateAsync(await getPatchData());
                } else {
                  await patchCustomsInstruction.mutateAsync(await getPatchData());
                }

                onSubmitSuccess();
                break;
              case 'attempt':
              case 'submit':
                if (isAdmin) {
                  setRequestDialogOpen(true);
                } else {
                  await patchCustomsInstruction.mutateAsync({
                    ...(await getPatchData()),
                    status_name: '送信済',
                  });

                  onSubmitSuccess();
                }
                break;
            }
          },
        })}
      >
        <CustomsInstructionContext.Provider value={contextValue}>
          <CustomsInstructionAlertDialog
            open={alertOpen}
            onClose={() => setAlertOpen(false)}
            parsed={parsed}
            showRcepAlert={showRcepAlert}
          />

          <InstructionContainer
            d2dNo={d2dNo}
            variant="customsInstruction"
            alert={
              <>
                {isOverdue && !isAdmin && (
                  <Alert
                    open
                    severity="error"
                    hideIcon
                    classes={{
                      container: clsx(classes.textErrorImportant, classes.fontWeight600Important),
                    }}
                  >
                    {strings.overdueMessage}
                  </Alert>
                )}

                {renderAlertsAndSpinnerModal()}

                <Alert severity="success" open={!!successfulAction}>
                  {successfulAction && strings.successMessage(d2dNo)[successfulAction]}
                </Alert>
              </>
            }
            left={<ManagementInfoBar />}
            right={isAdmin ? <AdminActionsBar /> : <ClientActionsBar />}
          >
            <div className={classes.topGuidance}>
              {strings.pleaseUploadCustomsFileAndCommodityInfo}
            </div>
            {!isAdmin && <CustomFilesInformation />}

            <ManagementFormContainer mode={mode}>
              <ManagementFormLabel field={fields.custom_files} required />
              <InstructionInputContainer>
                <FileArrayInput
                  field={fields.custom_files}
                  readOnly={!isEditing}
                  buttonProps={{
                    text: formStrings.selectFiles,
                    color: 'outlined',
                    className: classes.selfStart,
                  }}
                />

                <InstructionHint>{strings.customsFilesUploadHint}</InstructionHint>
              </InstructionInputContainer>

              <ManagementFormLabel field={fields.commodities} required />
              <InstructionInputContainer>
                <CommodityArrayInput2 field={fields.commodities} />
              </InstructionInputContainer>

              <CustomsInstructionSpecialNote />
            </ManagementFormContainer>

            <InstructionSplit />

            <ManagementFormContainer mode={mode}>
              <div style={{ gridColumn: '1 / -1', paddingTop: 30 }}>
                <FormTitle title={instructionStrings.seaInsurance} noEdit noExpand />
              </div>

              <CustomsInstructionInsurance />
            </ManagementFormContainer>

            {baseInquiryDetail.use_rcep && (
              <>
                <InstructionSplit />
                <ManagementFormContainer mode={mode}>
                  <div style={{ gridColumn: '1 / -1', paddingTop: 30 }}>
                    <FormTitle title={instructionStrings.rcepUsage} noEdit noExpand />
                  </div>

                  <ManagementFormLabel field={fields.rcep_file} />
                  <InstructionInputContainer>
                    <FileInput
                      field={fields.rcep_file}
                      buttonProps={{
                        text: formStrings.selectFiles,
                        color: 'outlined',
                        style: { alignSelf: 'start' },
                      }}
                      readOnly={!isEditing}
                    />

                    <InstructionHint>{strings.rcepFileUploadHint()}</InstructionHint>
                  </InstructionInputContainer>
                </ManagementFormContainer>
              </>
            )}

            <DebugForm form={form} />
          </InstructionContainer>
        </CustomsInstructionContext.Provider>
      </form>
    </>
  );
};

export interface CustomsInstructionEditDisplayProps extends CustomsInstructionContextValueBase {}

export const CustomsInstructionEditDisplay: React.FC<CustomsInstructionEditDisplayProps> = (
  props,
) => {
  const { d2dNo } = props;

  const isAdmin = useIsAdmin();

  const queryClient = useQueryClient();

  const customsInstructionQuery = isAdmin
    ? dashboardCustomsInstructionApis.useGet(
        '/dashboard/customs-instruction/:d2d_no',
        {
          params: { d2d_no: d2dNo },
        },
        {
          staleTime: Infinity,
        },
      )
    : customsInstructionApis.useGet(
        '/customs-instruction/:d2d_no',
        {
          params: { d2d_no: d2dNo },
        },
        {
          staleTime: Infinity,
        },
      );

  const adminBookingInquiryQuery = useBookingInquiryDetailByD2dNo(d2dNo, {
    enabled: isAdmin,
  });

  const adminPreAlertQuery = dashboardPreAlertApis.useGet(
    '/dashboard/pre-alert/:d2d_no',
    {
      params: {
        d2d_no: d2dNo,
      },
    },
    {
      enabled: isAdmin,
    },
  );

  const bookingInquiryQuery = useBookingInquiryDetail(d2dNo, {
    enabled: !isAdmin,
  });

  const userQuery = useUser();

  const onRefresh = useCallback(() => {
    customsInstructionQuery.invalidate();
    queryClient.invalidateQueries(
      dashboardBookingInquiryApis.getKeyByPath(
        'get',
        '/dashboard/booking-inquiry/:d2d_no/status-flows',
        {
          params: { d2d_no: d2dNo },
        },
      ),
    );

    queryClient.invalidateQueries(
      bookingInquiryApis.getKeyByPath('get', '/booking-inquiry/:d2d_no/status-flows', {
        params: { d2d_no: d2dNo },
      }),
    );

    queryClient.invalidateQueries([`/dashboard/booking-inquiry/${d2dNo}/`]);

    queryClient.invalidateQueries([`/booking-inquiry/${d2dNo}/`]);
  }, [d2dNo]);

  return (
    <Layout
      title={`${instructionStrings.customsInstruction} - ${d2dNo}`}
      hideCookiePolicy
      hideFooter
      hideHeader
    >
      <QueryContainer
        queries={[
          customsInstructionQuery,
          userQuery,
          adminBookingInquiryQuery,
          bookingInquiryQuery,
        ]}
      >
        <WithQueries
          {...props}
          customsInstruction={customsInstructionQuery.data!}
          user={userQuery.data!}
          adminPreAlert={adminPreAlertQuery.data}
          adminBookingInquiry={adminBookingInquiryQuery.data}
          baseInquiryDetail={bookingInquiryQuery.data}
          onRefresh={onRefresh}
        />
      </QueryContainer>
    </Layout>
  );
};
